import React from 'react';
import { Target, Zap, ShieldCheck, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLang } from '@/context/LanguageContext';
import { useOffer } from '@/context/OfferContext';
import { ScrollReveal } from '@/components/common/Primitives';
import { StatsCounter } from '@/components/common/StatsCounter';

export default function About() {
  const { t, lang } = useLang();
  const { openOffer } = useOffer();
  const values = [
    { icon: Zap, title: { de: 'Innovation', en: 'Innovation' }, text: { de: 'Wir denken Technologie aus der Zukunft – nicht aus der Vergangenheit.', en: 'We think technology from the future – not the past.' } },
    { icon: ShieldCheck, title: { de: 'Vertrauen', en: 'Trust' }, text: { de: 'Eigene Infrastruktur, transparente Prozesse und echte Ansprechpartner.', en: 'Own infrastructure, transparent processes and real contacts.' } },
    { icon: Target, title: { de: 'Kompetenz', en: 'Competence' }, text: { de: 'Tiefes Know-how über Hosting, JTL, Sicherheit und Softwareentwicklung.', en: 'Deep expertise in hosting, JTL, security and software development.' } },
    { icon: Users, title: { de: 'Partnerschaft', en: 'Partnership' }, text: { de: 'Wir wachsen mit unseren Kunden – langfristig und auf Augenhöhe.', en: 'We grow with our clients – long-term and as equals.' } },
  ];
  const stats = [
    { value: '500+', label: { de: 'Projekte', en: 'Projects' } },
    { value: '15+', label: { de: 'Jahre Erfahrung', en: 'Years experience' } },
    { value: '99.99%', label: { de: 'Verfügbarkeit', en: 'Uptime' } },
    { value: '24/7', label: { de: 'Support', en: 'Support' } },
  ];
  const tv = (o) => o[lang] || o.de;
  return (
    <div className="itw-noise">
      <section className="relative gradient-hero pt-32 lg:pt-44 pb-16 overflow-hidden">
        <div className="absolute inset-0 itw-grid opacity-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('about.title')}</h1>
            <p className="mt-5 text-lg text-[color:var(--itw-text-2)] max-w-2xl mx-auto">{t('about.subtitle')}</p>
          </ScrollReveal>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[color:var(--itw-bg)] to-transparent" />
      </section>

      <section className="relative z-10 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (<ScrollReveal key={i} delay={i * 0.08}><StatsCounter value={s.value} label={tv(s.label)} /></ScrollReveal>))}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="glass rounded-3xl p-8 sm:p-12 text-center neon-edge">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('about.missionTitle')}</h2>
              <p className="mt-4 text-[color:var(--itw-text-2)] leading-relaxed text-lg">{t('about.mission')}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal><h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)] text-center mb-12">{t('about.valuesTitle')}</h2></ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="glass rounded-2xl p-6 h-full">
                  <div className="h-12 w-12 rounded-xl bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] grid place-items-center"><v.icon className="h-6 w-6 text-[color:var(--itw-cyan)]" /></div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-[color:var(--itw-text)]">{tv(v.title)}</h3>
                  <p className="mt-2 text-sm text-[color:var(--itw-text-2)] leading-relaxed">{tv(v.text)}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button onClick={() => openOffer('')} data-testid="about-cta" className="h-12 px-8 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity rounded-full">{t('nav.requestOffer')} <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
